'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useId, useRef, useState, } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cx } from '../../../internal/cx.js';
/** Arrow keys move between tabs; Home/End jump to the ends. */
export const Tabs = TabsPrimitive.Root;
export const TabsList = forwardRef(function TabsList({ variant = 'line', scrollable = false, className, ...rest }, ref) {
    return (_jsx(TabsPrimitive.List, { ref: ref, ...rest, "data-variant": variant, "data-scrollable": scrollable || undefined, className: cx('mn-tabs__list', className) }));
});
function hasCount(count) {
    return count !== null && count !== undefined && count !== false && count !== '';
}
/** The count bubble shared by every trigger type. Part of the trigger's accessible name. */
function TabsCount({ count, tone }) {
    if (!hasCount(count))
        return null;
    return (_jsx("span", { className: "mn-tabs__count", "data-tone": tone, children: count }));
}
export const TabsTrigger = forwardRef(function TabsTrigger({ className, count, countTone = 'neutral', children, ...rest }, ref) {
    return (_jsx(TabsPrimitive.Trigger, { ref: ref, ...rest, className: cx('mn-tabs__trigger', className), children: hasCount(count) ? (_jsxs(_Fragment, { children: [children, _jsx(TabsCount, { count: count, tone: countTone })] })) : (children) }));
});
export const TabsContent = forwardRef(function TabsContent({ className, ...rest }, ref) {
    return (_jsx(TabsPrimitive.Content, { ref: ref, ...rest, className: cx('mn-tabs__content', className) }));
});
/**
 * Tab-styled navigation between pages: a `<nav>` of TabsLink. Each link is a
 * real link with its own URL, so it uses link semantics (`aria-current`), not
 * the tab role, and Tab moves between links.
 */
export const TabsNav = forwardRef(function TabsNav({ variant = 'line', scrollable = false, className, ...rest }, ref) {
    return (_jsx("nav", { ref: ref, ...rest, "data-variant": variant, "data-scrollable": scrollable || undefined, className: cx('mn-tabs__list', 'mn-tabs__nav', className) }));
});
export const TabsLink = forwardRef(function TabsLink({ active = false, asChild = false, count, countTone = 'neutral', className, children, ...rest }, ref) {
    const Comp = asChild ? Slot : 'a';
    return (_jsxs(Comp, { ref: ref, "aria-current": active ? 'page' : undefined, ...rest, "data-state": active ? 'active' : 'inactive', className: cx('mn-tabs__trigger', 'mn-tabs__link', className), children: [_jsx(Slottable, { children: children }), _jsx(TabsCount, { count: count, tone: countTone })] }));
});
/**
 * Tabs that filter one list instead of switching panels: "All 24 · Open 6 ·
 * Closed 18". Same keyboard model as Tabs (arrow keys, Home/End, roving
 * focus) and the same look, with one shared, optional panel.
 */
export const TabsFilter = forwardRef(function TabsFilter({ options, value: valueProp, defaultValue, onValueChange, variant = 'line', scrollable = false, activationMode = 'automatic', children, panelClassName, className, id, 'aria-label': label, ...rest }, ref) {
    const auto = useId();
    const base = id ?? `mn-tabs-filter-${auto}`;
    const panelId = `${base}-panel`;
    const tabId = (v) => `${base}-tab-${v.replace(/[^\w-]/g, '_')}`;
    const firstEnabled = options.find((o) => !o.disabled)?.value ?? '';
    const [inner, setInner] = useState(defaultValue ?? firstEnabled);
    const value = valueProp ?? inner;
    const hasPanel = children !== undefined && children !== null && children !== false;
    const buttons = useRef(new Map());
    // The tab in the Tab order: the selected one, or the first enabled tab
    // when the value matches no option.
    const tabbable = options.some((o) => o.value === value && !o.disabled) ? value : firstEnabled;
    const select = useCallback((next) => {
        if (next === value)
            return;
        if (valueProp === undefined)
            setInner(next);
        onValueChange?.(next);
    }, [value, valueProp, onValueChange]);
    const onKeyDown = (event, current) => {
        const enabled = options.filter((o) => !o.disabled);
        const i = enabled.findIndex((o) => o.value === current);
        const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
        let target;
        switch (event.key) {
            case 'ArrowRight':
                target = enabled[(i + (rtl ? -1 : 1) + enabled.length) % enabled.length];
                break;
            case 'ArrowLeft':
                target = enabled[(i + (rtl ? 1 : -1) + enabled.length) % enabled.length];
                break;
            case 'Home':
                target = enabled[0];
                break;
            case 'End':
                target = enabled[enabled.length - 1];
                break;
            default:
                return;
        }
        if (!target)
            return;
        event.preventDefault();
        buttons.current.get(target.value)?.focus();
        if (activationMode === 'automatic')
            select(target.value);
    };
    return (_jsxs("div", { ref: ref, ...rest, id: base, className: cx('mn-tabs-filter', className), children: [_jsx("div", { role: "tablist", "aria-label": label, "aria-orientation": "horizontal", "data-variant": variant, "data-scrollable": scrollable || undefined, className: "mn-tabs__list", children: options.map((o) => {
                    const selected = o.value === value;
                    return (_jsxs("button", { ref: (el) => {
                            if (el)
                                buttons.current.set(o.value, el);
                            else
                                buttons.current.delete(o.value);
                        }, type: "button", role: "tab", id: tabId(o.value), "aria-selected": selected, "aria-controls": hasPanel ? panelId : undefined, tabIndex: o.value === tabbable ? 0 : -1, disabled: o.disabled, "data-state": selected ? 'active' : 'inactive', "data-disabled": o.disabled ? '' : undefined, className: "mn-tabs__trigger", onClick: () => select(o.value), onKeyDown: (e) => onKeyDown(e, o.value), children: [o.label, _jsx(TabsCount, { count: o.count, tone: o.countTone ?? 'neutral' })] }, o.value));
                }) }), hasPanel ? (_jsx("div", { role: "tabpanel", id: panelId, "aria-labelledby": tabId(value), 
                // APG: a tab panel is focusable so its content can be reached by keyboard.
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex: 0, className: cx('mn-tabs__content', panelClassName), children: children })) : null] }));
});
/**
 * Binds Tabs or TabsFilter to the URL without importing a router: the app
 * passes the param it read and a setter, and spreads the result.
 *
 *   const [params, setParams] = useSearchParams();
 *   const tabs = useTabsUrlBinding({
 *     value: params.get('tab'),
 *     onChange: (v) => setParams(v ? { tab: v } : {}),
 *     values: ['overview', 'activity'],
 *     defaultValue: 'overview',
 *   });
 *   <Tabs {...tabs}>…</Tabs>
 */
export function useTabsUrlBinding({ value, onChange, values, defaultValue, clearDefault = true, }) {
    const current = values.includes(value ?? '') ? value : defaultValue;
    const onValueChange = useCallback((next) => {
        if (!values.includes(next))
            return;
        onChange(clearDefault && next === defaultValue ? null : next);
    }, [values, onChange, clearDefault, defaultValue]);
    return { value: current, onValueChange };
}
//# sourceMappingURL=tabs.js.map